import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue"
import type { BlockInstance } from "@dk/theme"
import {
  cartCountdownMinutes,
  cartCountdownText,
  cartShowSavings,
  defaultMainCartBlocks,
  mergeProductUpsellBlocks,
} from "@dk/theme"
import { api } from "./api"
import { displayFreeShipThreshold, displayPrice } from "./displayPrice"
import {
  MAX_PRODUCT_UPSELLS,
  pendingUpsellDefaults,
  readChoices,
  recordChoice,
  resolveProductUpsell,
  upsellChoiceKey,
} from "./productupsell"
import { cartBlockRegistry } from "../components/cart/registry"
import { provideCartDrawer, type CartDrawerContext, type UpsellItem } from "./cartdrawer"
import { useBootstrapStore } from "../stores/bootstrap"
import { useCartStore } from "../stores/cart"

/** Owns the state every `main-cart` block reads, and provides it.
 *
 *  The state used to live in CartDrawer.vue, which made the drawer the ONLY
 *  component that could host the blocks: `/cart` rendered the same `CartBlocks`
 *  list without providing anything, so all nine context-reading blocks threw in
 *  setup and the page rendered empty — an item in the cart and no way to check
 *  out from `/cart`. CartBlocks unified the markup; this unifies the state
 *  behind it, so the two hosts still differ only in the frame around them.
 *
 *  `active` is what the two hosts disagree about: the drawer's blocks are only
 *  live while it is open, the page's are live for as long as the page is. It
 *  gates the two things that cost something — the countdown interval and the
 *  upsell fetch — so a closed drawer stays exactly as idle as before.
 *
 *  Money stays server-authoritative; nothing here prices anything.
 */
export function useCartBlockHost(active: Ref<boolean>): CartDrawerContext {
  const cart = useCartStore()
  const bootstrap = useBootstrapStore()

  const empty = computed(() => !cart.lines.length)
  // The pre-merge "Theme settings → Cart" object. It is no longer editable anywhere —
  // the group is gone from the settings panel and every value lives on the block that
  // renders it — but a store carries it until its seller next opens the builder, and
  // while it EXISTS it is authoritative for the four settings whose block side used to
  // be dead. See @dk/theme's cart-settings.ts; `undefined` (not `{}`) is what tells
  // those resolvers this store is migrated.
  const legacy = computed(() => bootstrap.themeSettings.cart)
  const settings = computed(() => bootstrap.themeSettings.cart ?? {})

  // The seller's block order.
  const mainCartSection = computed(() =>
    (bootstrap.themeDoc?.pages?.cart?.sections ?? []).find((s) => s.type === "main-cart"),
  )
  const blocks = computed<BlockInstance[]>(() => {
    const sec = mainCartSection.value
    // A theme authored before the block system has no main-cart section — synthesize
    // the default order so those stores render exactly as they did, no migration.
    const list = sec?.blocks?.length
      ? sec.blocks
      : defaultMainCartBlocks().map((type) => ({ id: `default-${type}`, type, visible: true, settings: {} }))
    // A drawer authored before the upsell merge holds `upsells` and/or `add-ons`
    // where one `product-upsell` now belongs. The builder persists that rewrite on
    // the seller's next visit; this applies it at READ time, so a live store renders
    // one correct strip today rather than two dropped blocks. Pure — it clones the
    // survivor rather than writing into the bootstrap's theme document.
    return mergeProductUpsellBlocks(list).filter((b) => b.visible !== false && b.type in cartBlockRegistry)
  })

  // Gating for the optional policies-checkbox block. Default: not required, so a
  // cart without that block checks out exactly as before.
  const policiesOk = ref(false)
  const policiesRequired = ref(false)

  /** One block instance by type, for the four settings that moved onto their block. */
  const blockOf = (type: string) => blocks.value.find((b) => b.type === type)?.settings as
    | Record<string, unknown>
    | undefined

  // Free-shipping progress nudge (threshold is in major currency units → cents).
  // displayFreeShipThreshold wraps @dk/theme's cartFreeShipThreshold: a shipping
  // A/B arm's threshold when the visit is in one, the theme's otherwise — the
  // same number the arm's rate brackets charge (plan D6).
  const freeShip = computed(() => {
    const t = displayFreeShipThreshold(blockOf("progress-bar"), legacy.value)
    const threshold = t > 0 ? t * 100 : 0
    const remaining = Math.max(0, threshold - cart.subtotal)
    const pct = threshold > 0 ? Math.min(100, Math.round((cart.subtotal / threshold) * 100)) : 0
    return { threshold, remaining, pct }
  })

  // "You saved $X", from the lines alone. A volume bundle discounts the LINE now
  // (`original_total_cents` is its exact list value), so adding the pools' savings on
  // top would count every bundle saving twice — and `original_total_cents` is also
  // the only figure that survives a row split across packs at two prices, where
  // compare_at x quantity has no single unit price to multiply.
  const showSavings = computed(() => cartShowSavings(blockOf("savings"), legacy.value))
  const savingsCents = computed(() => {
    let s = 0
    for (const l of cart.lines) {
      if (l.original_total_cents && l.original_total_cents > l.line_total_cents) {
        s += l.original_total_cents - l.line_total_cents
        continue
      }
      const c = l.compare_at_price_cents ?? 0
      if (c > l.price_cents) s += (c - l.price_cents) * l.quantity
    }
    return s
  })

  // Cart reservation countdown — evergreen, per browser session (a nudge, not a
  // server-side hold). The end timestamp lives in sessionStorage so it survives
  // route changes but resets when the tab closes; it restarts once it hits zero.
  // Both hosts read the same key, so the drawer and `/cart` show one deadline.
  const CD_KEY = `dk:cart:cd:${location.host}`
  const cdEnd = ref(0)
  const nowMs = ref(Date.now())
  let cdTimer: ReturnType<typeof setInterval> | undefined
  const cdMinutes = computed(() => cartCountdownMinutes(blockOf("countdown"), legacy.value))
  const showCountdown = computed(() => cdMinutes.value > 0 && cdEnd.value > 0)
  const countdownLabel = computed(() => cartCountdownText(blockOf("countdown"), legacy.value))
  const countdownMMSS = computed(() => {
    const s = Math.max(0, Math.floor((cdEnd.value - nowMs.value) / 1000))
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`
  })
  function ensureCountdown() {
    if (cdMinutes.value <= 0) {
      cdEnd.value = 0
      return
    }
    let end = Number(sessionStorage.getItem(CD_KEY) || 0)
    if (!end || end <= Date.now()) {
      end = Date.now() + cdMinutes.value * 60_000
      sessionStorage.setItem(CD_KEY, String(end))
    }
    cdEnd.value = end
  }
  function tick() {
    nowMs.value = Date.now()
    if (cdEnd.value && nowMs.value >= cdEnd.value) ensureCountdown() // restart the window
  }

  // The `product-upsell` strip (products picked on the block, else the Cart theme
  // setting). Fetched when the host goes active.
  //
  // `selected` is resolved against the LIVE cart rather than baked in at fetch
  // time, which is what turns the strip from a list of Add buttons into a set of
  // switches: an offer the shopper accepted stays on the strip, switched on, and
  // pressing it again removes the line it added.
  type RawUpsell = Omit<UpsellItem, "selected" | "line_id">
  const upsellRaw = ref<RawUpsell[]>([])
  const upsellBusy = ref(0)
  const upsell = computed(() => resolveProductUpsell(blocks.value, settings.value))
  const upsellShown = computed<UpsellItem[]>(() => {
    if (!upsell.value.enabled) return []
    return upsellRaw.value.slice(0, MAX_PRODUCT_UPSELLS).map((p) => {
      // By VARIANT, not by handle: the row prices one variant, so it may only
      // claim to hold — and may only remove — that same variant's line.
      const line = cart.lines.find((l) => l.variant_id === p.variant_id)
      return { ...p, selected: Boolean(line), line_id: line?.line_id }
    })
  })
  async function loadUpsell() {
    if (!upsell.value.enabled) {
      upsellRaw.value = []
      return
    }
    const handles = upsell.value.handles
    if (!handles.length) {
      upsellRaw.value = []
      return
    }
    const out: RawUpsell[] = []
    await Promise.all(
      handles.map(async (h) => {
        try {
          const { product } = await api.product(h)
          const v = product.variants.find((x) => x.available) ?? product.variants[0]
          if (!v) return
          out.push({ variant_id: v.id, title: product.title, display_cents: displayPrice(v), compare_at_price_cents: v.compare_at_price_cents, image_src: product.images?.[0]?.src, product_handle: product.handle })
        } catch {
          /* skip a bad handle */
        }
      }),
    )
    upsellRaw.value = out
  }

  async function toggleUpsell(vid: number) {
    const p = upsellShown.value.find((x) => x.variant_id === vid)
    if (!p || upsellBusy.value) return
    upsellBusy.value = vid
    // Recorded BEFORE the request, not after: removing the line is what wakes the
    // pre-selection rule below, and it wakes on Vue's own queue — measured in the
    // browser, that job runs before this function resumes from its `await`, so a
    // decision written afterwards arrives too late and the rule puts the offer
    // straight back. Writing it first closes the window entirely. A request that
    // then fails leaves a decision the shopper really did express, which is the
    // safe way to be wrong.
    recordChoice(localStorage, cart.token, upsellChoiceKey(p.product_handle), !p.selected)
    try {
      if (p.selected && p.line_id) {
        await cart.removeLine(p.line_id)
      } else if (!p.selected) {
        // `reveal: false` — pressed from inside the cart; on /cart the default
        // would slide the drawer over the page being used.
        await cart.add(p.variant_id, 1, undefined, { reveal: false })
      }
    } finally {
      upsellBusy.value = 0
    }
  }

  // Seller-preselected offers, applied once per cart. Gated on the cart already
  // holding something the shopper chose themselves: pre-selecting into an empty cart
  // is not an upsell, it is building the cart for them. Sequential rather than
  // parallel — each add returns the whole authoritative cart, so concurrent adds
  // would race to overwrite one another.
  let upsellDefaultsRunning = false
  async function applyUpsellDefaults() {
    if (upsellDefaultsRunning || upsellBusy.value || !cart.token) return
    if (!upsell.value.defaultOn || !upsellShown.value.length) return
    const offered = new Set(upsellShown.value.map((p) => p.variant_id))
    if (!cart.lines.some((l) => !offered.has(l.variant_id))) return
    const wanted = pendingUpsellDefaults(upsellShown.value, readChoices(localStorage, cart.token))
    if (!wanted.length) return
    upsellDefaultsRunning = true
    try {
      for (const p of wanted) {
        // Re-check against the live cart: an earlier iteration refreshed it, and
        // the shopper may have toggled something while we were mid-flight.
        const live = upsellShown.value.find((x) => x.variant_id === p.variant_id)
        if (!live || live.selected) continue
        await cart.add(live.variant_id, 1, undefined, { reveal: false })
        // "Applied once per cart" has to be recorded, not inferred from the cart:
        // an offer is an ordinary line, so the shopper can also delete it from the
        // item list — where there is no switch to record anything — and an
        // unrecorded pre-selection would simply add it again.
        recordChoice(localStorage, cart.token, upsellChoiceKey(live.product_handle), true)
      }
    } catch {
      /* a pre-selection that fails is not worth an error to the shopper */
    } finally {
      upsellDefaultsRunning = false
    }
  }

  // `immediate` so a host that is active from the start (the cart PAGE) still
  // gets its countdown and upsells. For the drawer the first run lands on the
  // inactive branch — `cart.open` is false at mount — which is a no-op, so the
  // drawer keeps starting its timer at the same moment it always did.
  watch(
    active,
    (on) => {
      if (on) {
        nowMs.value = Date.now() // refresh before first tick so the initial MM:SS is exact
        ensureCountdown()
        if (cdMinutes.value > 0 && !cdTimer) cdTimer = setInterval(tick, 1000)
        // Chained rather than left to the watcher below: a REopened drawer
        // refetches the same three products, so `upsellRaw.length` does not
        // change and a pre-selection that failed the first time (no cart yet)
        // would never be retried.
        void loadUpsell().then(() => {
          if (active.value) void applyUpsellDefaults()
        })
      } else if (cdTimer) {
        clearInterval(cdTimer)
        cdTimer = undefined
      }
    },
    { immediate: true },
  )

  // Live-preview / settings changes while the host is active (builder).
  // Watched through the RESOLVED config, and by value rather than by identity: the
  // computed re-runs whenever any cart setting is edited in the builder, so keying
  // off the object would re-fetch the same three products on every unrelated
  // keystroke in that panel.
  watch(
    [() => upsell.value.enabled, () => upsell.value.handles.join("\n")],
    () => {
      if (active.value) void loadUpsell()
    },
  )

  // The activation watcher above can fire before the cart exists — /cart mounts
  // active while `cart.load()` is still in flight, and the drawer opens on the same
  // tick as the add that created the cart. Re-running when the cart or the token
  // changes is what makes the pre-selection land in both cases; the rule is
  // idempotent (the ledger and the `selected` re-check see to that).
  //
  // `upsellRaw.length` is in the list because the offers arrive from a fetch that
  // finishes AFTER activation — without it a pre-selected strip would only ever
  // apply on some later cart change.
  watch(
    [() => cart.token, () => cart.lines.length, () => upsellRaw.value.length],
    () => {
      if (!active.value) return
      void applyUpsellDefaults()
    },
  )

  onBeforeUnmount(() => {
    if (cdTimer) clearInterval(cdTimer)
  })

  const ctx: CartDrawerContext = {
    cart: cart as unknown as Record<string, unknown>,
    empty,
    settings,
    countdown: computed(() => ({
      show: showCountdown.value,
      label: countdownLabel.value,
      mmss: countdownMMSS.value,
    })),
    freeShip,
    // Zero when the theme toggle is off, so the savings block stays hidden.
    savingsCents: computed(() => (showSavings.value ? savingsCents.value : 0)),
    upsellItems: upsellShown,
    toggleUpsell,
    upsellBusy,
    policiesOk,
    policiesRequired,
    blocks,
  }
  provideCartDrawer(ctx)
  return ctx
}
